import type { Lang } from './config';

const synth = (): SpeechSynthesis | null =>
	'speechSynthesis' in window ? window.speechSynthesis : null;

const BASES: Record< Lang, string[] > = {
	he: [ 'he', 'iw' ],
	en: [ 'en' ],
	ar: [ 'ar' ],
	ru: [ 'ru' ],
};

/**
 * Voices that run on the device (`localService`) and match the page language. Remote voices are never used.
 * @param lang
 */
export const localVoices = ( lang: Lang ): SpeechSynthesisVoice[] => {
	const s = synth();
	if ( ! s ) {
		return [];
	}
	return s
		.getVoices()
		.filter(
			( v ) =>
				v.localService === true &&
				BASES[ lang ].includes(
					v.lang.toLowerCase().split( /[-_]/ )[ 0 ] ?? ''
				)
		);
};

export const onVoicesChanged = ( cb: () => void ): void => {
	synth()?.addEventListener?.( 'voiceschanged', cb );
};

const chunks = ( text: string ): string[] => {
	const parts =
		text
			.replace( /\s+/g, ' ' )
			.trim()
			.match( /[^.!?؟。]+[.!?؟。]*\s*/g ) ?? [];
	const out: string[] = [];
	let cur = '';
	for ( const part of parts ) {
		if ( ( cur + part ).length > 180 && cur ) {
			out.push( cur );
			cur = '';
		}
		cur += part;
	}
	return cur ? [ ...out, cur ] : out;
};

export const pageText = (): string => {
	const selected = document.defaultView?.getSelection()?.toString().trim();
	if ( selected ) {
		return selected;
	}
	const main =
		document.querySelector< HTMLElement >( 'main, [role=main], article' ) ??
		document.body;
	return main.innerText;
};

export const speak = (
	text: string,
	lang: Lang,
	pageLang: string
): boolean => {
	const s = synth();
	const voice = localVoices( lang )[ 0 ];
	if ( ! s || ! voice ) {
		return false;
	}
	s.cancel();
	for ( const part of chunks( text ) ) {
		const u = new SpeechSynthesisUtterance( part );
		u.voice = voice;
		u.lang = voice.lang || pageLang;
		s.speak( u );
	}
	return true;
};

export const stopSpeaking = (): void => {
	synth()?.cancel();
};
